import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TextField,
    Chip,
    Skeleton,
    Button,
    Tabs,
    Tab,
    IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
    useGetPayoutsStatsQuery,
    useGetPayoutsBookingListQuery,
    useGetPayoutsBalanceQuery,
} from "../../service/earnings";
import SearchBar from "../../components/SearchBar";
import dayjs from "dayjs";
import { isValidInput } from "../../utils/validations";
import { PAYMENT_STATUS, BOOKING_STATUS } from "../../utils/enums";
import CustomPagination from "../../components/CustomPagination";
import CurrencyIcon from "../../components/dirham";

const Earnings = () => {
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [appliedStartDate, setAppliedStartDate] = useState("");
    const [appliedEndDate, setAppliedEndDate] = useState("");
    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(10);
    const [activeTab, setActiveTab] = useState(0);

    // ✅ Stats - /sp/payouts/stats (always fetched)
    const {
        data: statsResponse,
        isLoading: statsLoading,
    } = useGetPayoutsStatsQuery();

    // ✅ Schedules Tab - /sp/booking/list
    const {
        data: schedulesResponse,
        isLoading: schedulesLoading,
        isError: schedulesError,
    } = useGetPayoutsBookingListQuery(
        {
            payoutStartDate: appliedStartDate || undefined,
            payoutEndDate: appliedEndDate || undefined,
            search: debouncedSearchTerm || undefined,
            page: page,
            limit: rowsPerPage,
            isScheduled: true,
        },
        { skip: activeTab !== 1 }
    );

    // ✅ Payout History Tab - /sp/booking/list
    const {
        data: payoutHistoryResponse,
        isLoading: payoutHistoryLoading,
        isError: payoutHistoryError,
    } = useGetPayoutsBookingListQuery(
        {
            payoutStartDate: appliedStartDate || undefined,
            payoutEndDate: appliedEndDate || undefined,
            search: debouncedSearchTerm || undefined,
            page: page,
            limit: rowsPerPage,
            payoutPaid: true,
        },
        { skip: activeTab !== 0 }
    );

    // ✅ Balance Tab - /sp/booking/list (status: COMPLETED)
    const {
        data: balanceResponse,
        isLoading: balanceLoading,
        isError: balanceError,
    } = useGetPayoutsBookingListQuery(
        {
            payoutStartDate: appliedStartDate || undefined,
            payoutEndDate: appliedEndDate || undefined,
            search: debouncedSearchTerm || undefined,
            page: page,
            limit: rowsPerPage,
            status: BOOKING_STATUS.COMPLETED,
            payoutPaid: false,
        },
        { skip: activeTab !== 2 }
    );

    // Stats values (common field names covered)
    const stats = statsResponse?.data || {};
    const statCards = [
        {
            label: "Total Payouts Received",
            value: stats.totalPayout ?? 0,
            color: "#2E7D32",
            bg: "#E8F5E9",
            isCount: false,
        },
        {
            label: "Completed Transactions",
            value: stats.paid ?? 0,
            color: "#0288D1",
            bg: "#E1F5FE",
            isCount: true,
        },
        {
            label: "Pending Payouts",
            value: stats.pending ?? 0,
            color: "#F57C00",
            bg: "#FFF8E1",
            isCount: false,
        },
        {
            label: "Active Schedules",
            value: stats.scheduled ?? 0,
            color: "#9C27B0",
            bg: "#F3E5F5",
            isCount: true,
        }
    ];

    // ✅ Active data
    let activeResponse: any = schedulesResponse;
    if (activeTab === 0) activeResponse = payoutHistoryResponse;
    if (activeTab === 2) activeResponse = balanceResponse;

    const rawCompletedData = activeResponse?.data?.list || activeResponse?.data || [];
    let activeData = Array.isArray(rawCompletedData) ? rawCompletedData : [];

    const totalRecords = activeResponse?.data?.total || activeData.length;

    const isLoading = activeTab === 0 ? payoutHistoryLoading : activeTab === 1 ? schedulesLoading : balanceLoading;
    const isError = activeTab === 0 ? payoutHistoryError : activeTab === 1 ? schedulesError : balanceError;

    const getStatusLabel = (statusId: any) => {
        const id = Number(statusId);
        switch (id) {
            case PAYMENT_STATUS.PAID: return "Paid";
            case PAYMENT_STATUS.PENDING: return "Scheduled";
            case PAYMENT_STATUS.FAILED: return "Failed";
            case PAYMENT_STATUS.REFUND_PROGRESS: return "Refund in Progress";
            case PAYMENT_STATUS.REFUNDED: return "Refunded";
            case PAYMENT_STATUS.NON_REFUNDABLE: return "Non Refundable";
            default: return String(statusId) || "N/A";
        }
    };

    const getStatusColor = (status: string) => {
        const s = status?.toLowerCase();
        if (s === "completed" || s === "paid") return "success";
        if (s === "pending" || s === "scheduled" || s === "refund in progress") return "warning";
        if (s === "failed") return "error";
        return "default";
    };

    const renderTableRows = (colSpan: number) => {
        if (isLoading) {
            return (
                <TableRow>
                    <TableCell colSpan={colSpan} align="center">
                        Loading completed payouts...
                    </TableCell>
                </TableRow>
            );
        }
        if (isError) {
            return (
                <TableRow>
                    <TableCell colSpan={colSpan} align="center">
                        Failed to load records.
                    </TableCell>
                </TableRow>
            );
        }
        if (activeData.length === 0) {
            const emptyText = activeTab === 2
                ? "No pending payouts found."
                : (activeTab === 1 ? "No scheduled payouts found." : "No payouts found.");
            return (
                <TableRow>
                    <TableCell colSpan={colSpan} align="center">
                        {emptyText}
                    </TableCell>
                </TableRow>
            );
        }
        return activeData.map((item: any, idx: number) => {
            const amount = Number(item.payoutAmount || item.netPayout) || 0;
            let status = getStatusLabel(item.status);
            if (item.hasOwnProperty("payoutPaid")) {
                status = item.payoutPaid ? "Paid" : "Scheduled";
            }
            const payoutDate = item.payoutPaidAt || item.payoutScheduledDate || item.payoutDate;
            const createdAt = item.createdAt || item.date;
            const transactionId = item.transactionId || "—";

            return (
                <TableRow hover key={item.id || idx}>
                    <TableCell>{(page - 1) * rowsPerPage + idx + 1}</TableCell>
                    <TableCell>
                        {item.service ? (
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.service.name}</Typography>
                                {item.service.category?.name && (
                                    <Typography variant="caption" color="text.secondary">
                                        {item.service.category.name}
                                    </Typography>
                                )}
                            </Box>
                        ) : "—"}
                    </TableCell>
                    <TableCell><CurrencyIcon width={12} height={12} style={{ verticalAlign: "baseline" }} />{amount.toFixed(2)}</TableCell>
                    {activeTab !== 2 && (
                        <TableCell>
                            {payoutDate ? dayjs(payoutDate).format("DD MMM YYYY") : "—"}
                        </TableCell>
                    )}
                    {activeTab !== 2 && (
                        <TableCell>
                            <Chip
                                label={status}
                                color={getStatusColor(status)}
                                size="small"
                            />
                        </TableCell>
                    )}
                    {activeTab === 0 && <TableCell>{transactionId}</TableCell>}
                    {activeTab !== 2 && (
                        <TableCell>
                            {createdAt ? dayjs(createdAt).format("DD MMM YYYY") : "—"}
                        </TableCell>
                    )}
                    {activeTab === 2 && (
                        <TableCell>
                            <IconButton size="small" onClick={() => navigate(`/order-management/view/${item.id || item._id}`)}>
                                <VisibilityIcon color="action" fontSize="small" />
                            </IconButton>
                        </TableCell>
                    )}
                </TableRow>
            );
        });
    };

    return (
        <>
            <div className="main_title">
                <h1>My Earnings</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> - My Earnings
                </p>
            </div>

            {/* ===== STATS CARDS ===== */}
            <div className="cards">
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
                    gap={2}
                    mb={3} >
                    {statCards.map((card) => (
                        <Box
                            key={card.label}
                            p={2.5}
                            borderRadius="12px"
                            bgcolor={card.bg}
                            display="flex"
                            flexDirection="column"
                            gap={0.5}
                        >
                            <Typography
                                variant="caption"
                                fontWeight={600}
                                color="text.secondary"
                                textTransform="uppercase"
                                letterSpacing={0.5}>
                                {card.label}
                            </Typography>
                            {statsLoading ? (
                                <Skeleton width={80} height={32} />
                            ) : (
                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    color={card.color} >
                                    {card.isCount ? Number(card.value) : (
                                        <><CurrencyIcon width={16} height={16} style={{ verticalAlign: "baseline" }} />{Number(card.value).toFixed(2)}</>
                                    )}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>

                {/* ===== TABS ===== */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, newValue) => {
                            setActiveTab(newValue);
                            setPage(1);
                        }}
                        aria-label="earnings tabs">
                        <Tab label="Payout History" />
                        <Tab label="Schedules" />
                        <Tab label="Balances" />
                    </Tabs>
                </Box>

                {/* ===== FILTERS ===== */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                    mt={2}
                >
                    <SearchBar
                        value={searchTerm}
                        onChange={(e: any) => isValidInput(e.target.value) && setSearchTerm(e.target.value)}
                        onCross={() => {
                            setSearchTerm("");
                            setDebouncedSearchTerm("");
                        }}
                        searchTerm={searchTerm}
                        setDebouncedSearchTerm={setDebouncedSearchTerm}
                        placeholder="Search amt or txn id..."
                    />
                    {activeTab !== 2 && (
                        <>
                            <div className="form" style={{ marginLeft: "auto" }}>
                                <TextField
                                    type="date"
                                    label="Start Date"
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: dayjs().format("YYYY-MM-DD") }}
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    sx={{ width: 200 }}
                                />
                            </div>
                            <div className="form">
                                <TextField
                                    type="date"
                                    label="End Date"
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        max: dayjs().format("YYYY-MM-DD"),
                                        min: startDate || undefined,
                                    }}
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    sx={{ width: 200 }}
                                />
                            </div>
                            <Box display="flex" gap={1}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        setAppliedStartDate(startDate);
                                        setAppliedEndDate(endDate);
                                        setPage(1);
                                    }}
                                    disabled={startDate === appliedStartDate && endDate === appliedEndDate}
                                >
                                    Apply
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    onClick={() => {
                                        setStartDate("");
                                        setEndDate("");
                                        setAppliedStartDate("");
                                        setAppliedEndDate("");
                                        setPage(1);
                                    }}
                                    disabled={!startDate && !endDate && !appliedStartDate && !appliedEndDate}
                                >
                                    Reset
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>

                {/* ===== TABLE ===== */}
                <TableContainer className="table_container" sx={{ mt: 3 }}>
                    <Table
                        sx={{ minWidth: 750 }}
                        aria-label="payouts table"
                        className="v3"
                    >
                        <TableHead>
                            <TableRow hover>
                                <TableCell>Sr. No.</TableCell>
                                <TableCell>Service</TableCell>
                                <TableCell>{activeTab === 2 ? "Pending Amount" : "Payout Amount"}</TableCell>
                                {activeTab !== 2 && <TableCell>Payout Date</TableCell>}
                                {activeTab !== 2 && <TableCell>Status</TableCell>}
                                {activeTab === 0 && <TableCell>Transaction ID</TableCell>}
                                {activeTab !== 2 && <TableCell>Payout Scheduled On</TableCell>}
                                {activeTab === 2 && <TableCell>Action</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>{renderTableRows(activeTab === 0 ? 7 : activeTab === 2 ? 4 : 6)}</TableBody>
                    </Table>
                </TableContainer>

                {totalRecords > 0 && (
                    <CustomPagination
                        currentPage={page - 1}
                        totalPages={Math.ceil(totalRecords / rowsPerPage)}
                        count={totalRecords}
                        pageSize={rowsPerPage}
                        onPageChange={(newPage) => setPage(newPage + 1)}
                    />
                )}
            </div>
        </>
    );
};

export default Earnings;
